import React, { Component } from "react";
import { connect } from "react-redux";
import { showModal } from "../../redux/actions/modalActions";
import { deleteItemFromList } from "../../redux/actions/listActions";
import { getWordyDate, findColesAndWoolsPricing } from "../../utils/UIFunctions";
import { Icon } from "../icons/Icon";
import ListOptionsModalMobile from "./listOptions/ListOptionsModalMobile";
import UnlockPremiumModal from "../popups/UnlockPremiumModal";
import DeleteListItemModalContent from "./listOptions/DeleteListItemModalContent";

interface Props {
  listMetaData: List;
  showModal?: any;
  user: UserState;
  deleteItemFromList: (list_id: number, item_id: string | number) => Promise<any>;
}

interface State {
  expandedView: boolean;
}

class ListCardMobile extends Component<Props, State> {
  state = {
    expandedView: false,
  };

  componentDidMount() {
    // Listen for the custom event from the options menu
    document.addEventListener('viewListItems', this.handleViewListItems);
  }

  componentWillUnmount() {
    // Clean up the event listener
    document.removeEventListener('viewListItems', this.handleViewListItems);
  }

  // Handle the custom event from options menu
  handleViewListItems = (event: any) => {
    if (event.detail && event.detail.listId === this.props.listMetaData.id) {
      // Toggle expanded view
      this.setState(prevState => ({
        expandedView: !prevState.expandedView
      }));
    }
  };
  
  handleDeleteItem = (list_id: number, item_id: string | number, item_name: string, list_name: string) => {
    const modalContent = () => (
      <DeleteListItemModalContent 
        list_id={list_id}
        item_id={item_id}
        item_name={item_name}
        list_name={list_name}
      />
    );
    
    this.props.showModal({ component: modalContent });
  };
  
  clickedOptions = (list: List | undefined) => {
    if (list) {
      const modalContent = () =>
          this.props.user?.isPremium ? (
              <ListOptionsModalMobile list={list} />
          ) : (
              <UnlockPremiumModal />
          );
      this.props.showModal({ component: modalContent });
    }
  };

  toggleExpandView = (e?: React.MouseEvent) => {
    // If event exists, prevent default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    this.setState(prevState => ({
      expandedView: !prevState.expandedView
    }));
  };
  
  renderListItems = (list) => {
    const items = list.items || [];
    
    if (items.length === 0) {
      return (
        <div className="u-text-center u-p--16 u-background--grey-light">
          <div className="u-opacity--7 u-main-font--small">
            No items in this list
          </div>
        </div>
      );
    }
    
    return (
      <div className="u-w-all">
        <div className="u-background--white">
          {items.map((item, index) => {
            const { colesPricing, woolsPricing } = findColesAndWoolsPricing(item);
            
            return (
              <div key={index} className="u-border--bottom--grey u-p--12">
                {/* Item name and image row */}
                <div className="u-flex u-mb--8">
                  {/* Item image - absolute left positioning */}
                  <div style={{ width: '40px', marginRight: '12px' }}>
                    {item.image_url ? (
                      <img 
                        src={item.image_url} 
                        alt={item.name} 
                        style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                      />
                    ) : (
                      <div style={{ width: '40px', height: '40px' }} className="u-background--grey-light u-flex u-justify-center u-align-center">
                        <Icon id="grocerize" size="s" stroke={"#aaa"} />
                      </div>
                    )}
                  </div>
                  
                  {/* Item name and quantity */}
                  <div className="u-flex-grow--1">
                    <div className="u-main-font--small u-semi-bold">
                      {item.name}
                    </div>
                    <div className="u-main-font--vsmall u-color-charcoal u-mt--4">
                      Qty: {item.quantity || 1}
                    </div>
                  </div>
                </div>
                
                {/* Item details and delete button */}
                <div className="u-flex u-justify-between u-mb--4">
                  <div className="u-flex-grow--1">
                    {/* Price comparison */}
                    <div className="u-flex" style={{ gap: '16px' }}>
                      {/* C price */}
                      <div style={{ 
                        backgroundColor: '#f9f9f9', 
                        padding: '8px 12px', 
                        borderRadius: '4px', 
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        width: '140px',
                        border: '1px solid #eee'
                      }}>
                        <div className="u-flex">
                          <span className="u-color-coles u-bold">C</span>
                          <span className="u-ml--8 u-bold">
                            {colesPricing && colesPricing.available 
                              ? `$${colesPricing.price?.toFixed(2) || "-"}` 
                              : <span className="u-color-grey">N/A</span>}
                          </span>
                        </div>
                        {/* Cup price row */}
                        {colesPricing?.cup_price && (
                          <div className="u-main-font--vsmall u-color-charcoal u-mt--2">
                            ${colesPricing.cup_price.toFixed(2)}/{colesPricing.cup_volume || '100g'}
                          </div>
                        )}
                      </div>
                      
                      {/* W price */}
                      <div style={{ 
                        backgroundColor: '#f9f9f9', 
                        padding: '8px 12px', 
                        borderRadius: '4px', 
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        width: '140px',
                        border: '1px solid #eee'
                      }}>
                        <div className="u-flex">
                          <span className="u-color-woolworths u-bold">W</span>
                          <span className="u-ml--8 u-bold">
                            {woolsPricing && woolsPricing.available 
                              ? `$${woolsPricing.price?.toFixed(2) || "-"}` 
                              : <span className="u-color-grey">N/A</span>}
                          </span>
                        </div>
                        {/* W Cup price */}
                        {woolsPricing?.cup_price && (
                          <div className="u-main-font--vsmall u-color-charcoal u-mt--2">
                            ${woolsPricing.cup_price.toFixed(2)}/{woolsPricing.cup_volume || '100g'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Single delete button for the whole item */}
                  <div>
                    <button 
                      className="u-button--icon u-color-red-hover"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.handleDeleteItem(list.id, item.id || item.shopify_id, item.name, list.title);
                      }}
                      aria-label="Remove item"
                    >
                      <span style={{ fontWeight: 'bold' }}>✕</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    const list = this.props.listMetaData;

    return (
      <div className="u-background--white u-ph--12 u-pv--24 u-border--bottom--darkgrey" data-list-id={list.id}>
        <div className="u-w-all u-flex u-justify-between">
          <div className="u-main-font--small u-bold">
            {list.title}{" "}
            <span className="u-color-grey u-semi-bold u-main-font--vsmall u-ph--12">
              {getWordyDate(list.updatedAt)}
            </span>
          </div>
          <div>
            <button onClick={() => this.clickedOptions(list)}>
              <Icon id="threedots" />
            </button>
          </div>
        </div>
        <div className="u-flex u-pt--12">
          <div className="u-main-font--small u-semi-bold u-w-all u-m--2 u-background--grey u-p--6 u-br--small">
            <div
              className="u-flex u-justify-center u-w-all"
              style={{ fontSize: "12px" }}
            >
              Items
            </div>
            <div className="u-flex u-justify-center u-w-all u-bold u-pt--4">
              {list.itemsCount}
            </div>
          </div>
          <div className="u-main-font--small u-semi-bold u-w-all u-m--2 u-background--grey u-p--6 u-br--small">
            <div
              className="u-text-right u-ph--12 u-w-all"
              style={{ fontSize: "12px" }}
            >
              Total
            </div>
            <div className="u-text-right u-ph--12 u-w-all u-bold u-pt--4">
              ${list?.grocerizeShopTotal.toFixed(2)}
            </div>
          </div>
          <div className="u-main-font--small u-bold u-w-all u-m--2 u-background--magenta u-color-white u-p--6 u-br--small">
            <div
              className="u-text-right u-ph--12 u-w-all u-align-center u-flex u-justify-flex-end"
              style={{ fontSize: "12px" }}
            >
              <Icon id="grocerize" size="xs" />
              <span className="u-ph--4"> Savings</span>
            </div>
            <div className="u-text-right u-ph--12 u-w-all u-bold u-pt--4">
              ${list?.grocerizeTotalSavings.toFixed(2)}
            </div>
          </div>
        </div>
        
        <div className="u-w-all u-flex u-justify-end u-mt--12 u-pr--12">
          <button 
            className="u-btn--pill u-pointer u-pr--8"
            style={{ fontSize: '100%' }}
            onClick={this.toggleExpandView}
          >
            {this.state.expandedView ? 'Hide List' : 'View List'} 
            <Icon id="chevron_right" size="xs" stroke="#9c2bad" fill="none" />
          </button>
        </div>
        
        {this.state.expandedView && (
          <div className="u-hide--desktop u-ph--8 u-mt--8">
            {this.renderListItems(list)}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.user,
});

export default connect(mapStateToProps, { 
  showModal,
  deleteItemFromList 
})(ListCardMobile);
